import {
  AuthContextProvider,
  type AuthListener,
  AuthThemeProvider,
  authService,
} from '@kitopi-com/customer-auth';
import {
  type CustomerProfileConfig,
  CustomerProfileContextProvider,
  useCustomerProfileApi,
} from '@kitopi-com/customer-profile';
import * as Sentry from '@sentry/react';
import privacyPolicyUrl from 'assets/legal/privacy-policy.pdf';
import termsAndConditionsUrl from 'assets/legal/terms-and-conditions.pdf';
import { useAccessPoint } from 'context/AccessPointContext';
import eruda from 'eruda';
import { useFirebaseCountries } from 'hooks/useFirebaseCountries';
import { useHandleLogin } from 'hooks/useHandleLogin';
import { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router';
import { useLocalStorage } from 'usehooks-ts';
import { AWS_CONFIG, CUSTOMER_PROFILE_API_URL, isDevMode, RECAPTCHA_KEY } from 'utils/constants';
import { getAllParamsFormUrl } from 'utils/getAllParamsFormUrl';
import { getDefaultCountry } from 'utils/getDefaultCountry';
import { customTheme } from '../auth/config/theme';
import i18n from '../translations/';
import { AppRoutes } from './AppRoutes/AppRoutes';

const CUSTOMER_PROFILE_CONFIG: CustomerProfileConfig = {
  baseURL: CUSTOMER_PROFILE_API_URL,
  getAuthToken: async () => {
    try {
      const { idToken, userId, internalAccountId } = await authService.getCustomerAuthSession();
      return {
        ...(idToken && { idToken: idToken.toString() }),
        ...(userId && { userId }),
        ...(internalAccountId && { internalAccountId }),
      };
    } catch (error) {
      console.info('Failed to get auth session for Customer Profile API:', error);
      return {};
    }
  },
};

const AppInner = () => {
  const countries = useFirebaseCountries();
  const { siteName } = getAllParamsFormUrl();
  const defaultCountry = getDefaultCountry({ supportedCountries: countries, siteName });
  const [locale, setLocale] = useLocalStorage('kitopi-com-auth:locale', 'en');
  const [apAuthError, setApAuthError] = useState<string | undefined>(undefined);
  const { setIsStartAuthorizingToAP, setIsAuthorizationRequestSuccess, setIsWaitingForAuthStatus } =
    useAccessPoint();

  const login = useHandleLogin({
    setApAuthError,
    setIsStartAuthorizingToAP,
    setIsAuthorizationRequestSuccess,
  });

  const changeLanguage = (language: string) => {
    setLocale(language);
  };

  if (isDevMode) {
    eruda.init();
  }

  // Access to Customer Profile API - now inside CustomerProfileContextProvider
  const customerProfileApi = useCustomerProfileApi();

  const authListener: AuthListener = {
    onSignIn: async context => {
      // 1. First - update customer profile (MUST complete before WiFi)
      if (context.state.customFieldsValues) {
        const { name, communicationEmail } = context.state.customFieldsValues;

        if (name || communicationEmail) {
          try {
            const updateData: { name?: string; communicationEmail?: string } = {};
            if (name) updateData.name = name;
            if (communicationEmail) updateData.communicationEmail = communicationEmail;
            setApAuthError('profileUpdateError');
            await customerProfileApi.updateCustomerProfile(updateData);
          } catch (error) {
            console.error('Failed to update customer profile:', error);
            setApAuthError('profileUpdateError');
            return; // Stop process - don't proceed to WiFi login
          }
        }
      }

      // 2. Set Sentry user context for error tracking
      if (process.env.SENTRY_DSN) {
        try {
          const attrs = await authService.getCustomerAuthAttributes();
          Sentry.setUser({
            ...(attrs.email.value && { email: attrs.email.value }),
            ...(attrs.phoneNumber.value && { username: attrs.phoneNumber.value }),
          });
        } catch {
          // non-blocking
        }
      }

      // 3. Only then - WiFi login
      try {
        await login();
      } catch {
        // login() already sets apAuthError state internally before throwing
        return;
      }
      setIsWaitingForAuthStatus(true);
    },
  };

  useEffect(() => {
    i18n.changeLanguage(locale);
  }, [locale]);

  useEffect(() => {
    if (apAuthError) {
      setIsWaitingForAuthStatus(false);
      setIsStartAuthorizingToAP(false);
    }
  }, [apAuthError]);

  return (
    <BrowserRouter>
      <AuthContextProvider
        key={defaultCountry ?? 'default'}
        authConfig={AWS_CONFIG}
        locale={locale}
        authListener={authListener}
        disableToasts
        privacyPolicyUrl={privacyPolicyUrl}
        termsAndConditionsUrl={termsAndConditionsUrl}
        supportedCountries={countries}
        {...(defaultCountry && { defaultCountry })}
        {...(RECAPTCHA_KEY && { recaptchaKey: RECAPTCHA_KEY })}
      >
        <AuthThemeProvider customTheme={customTheme}>
          <AppRoutes changeLanguage={changeLanguage} />
        </AuthThemeProvider>
      </AuthContextProvider>
    </BrowserRouter>
  );
};

export const App = () => {
  return (
    <CustomerProfileContextProvider config={CUSTOMER_PROFILE_CONFIG}>
      <AppInner />
    </CustomerProfileContextProvider>
  );
};
